import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper, Skeleton, Card, CardContent, Button } from '@mui/material';
import dayjs from 'dayjs';
import { IconButton } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeSelector from './DateRangeSelector';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BuyerDashboard = ({ username, dateRange, onDateRangeChange, userId }) => {
  const [records, setRecords] = useState([]);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpensesAgn, setTotalExpensesAgn] = useState(0);
  const [totalExpensesAcc, setTotalExpensesAcc] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalRoi, setTotalRoi] = useState(0);
  const [reject, setReject] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isDateRangeSelectorOpen, setDateRangeSelectorOpen] = useState(false);
  const [activeChartLine, setActiveChartLine] = useState('both');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  const { startDate, endDate } = dateRange || {};

  const handleDateRangeSelected = (range) => {
    onDateRangeChange(range);
    setDateRangeSelectorOpen(false);
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      setCurrentPage(1);
      try {
        const response = await axios.get(`/api/buyer/${username}/records`, {
          params: {
            startDate: dayjs(startDate).format('YYYY-MM-DD'),
            endDate: dayjs(endDate).format('YYYY-MM-DD'),
            userId,
          }
        });
        const { records, totalIncome, totalExpensesAgn, totalExpensesAcc, totalProfit, totalRoi, reject } = response.data || {};
        setTimeout(() => {
          setRecords(records);
          setTotalIncome(totalIncome);
          setTotalExpensesAgn(totalExpensesAgn);
          setTotalExpensesAcc(totalExpensesAcc);
          setTotalProfit(totalProfit);
          setTotalRoi(totalRoi);
          setReject(reject);
          setLoading(false);
        }, 900);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    if (startDate && endDate) {
      fetchRecords();
    }
  }, [username, startDate, endDate, userId]);

  const formatCurrency = (value) => {
    return value < 0 ? `-$${Math.abs(value)}` : `$${value}`;
  };

  const getRowStyle = (profit) => {
    const profitNum = parseFloat(String(profit).replace(/[^0-9.-]/g, ''));
    if (profitNum > 0) return { backgroundColor: '#e8f5e9' };
    if (profitNum < 0) return { backgroundColor: '#ffebee' };
    return {};
  };

  const chartData = records.map(record => ({
    date: record.date,
    Profit: parseFloat(String(record.profit).replace(/[^0-9.-]/g, '')) || 0,
    Revenue: parseFloat(String(record.income).replace(/[^0-9.-]/g, '')) || 0,
    Spent: (parseFloat(record.expensesAgn) || 0) + (parseFloat(record.expensesAcc) || 0),
  }));

  const tabStyle = (active) => ({
    backgroundColor: active ? '#1976d2' : '#e0e0e0',
    color: active ? '#fff' : '#333',
    marginRight: '8px',
    textTransform: 'none',
    minWidth: '80px',
  });

  const totalSpent = (totalExpensesAgn || 0) + (totalExpensesAcc || 0);

  const totalPages = Math.ceil(records.length / itemsPerPage);
  const paginatedRecords = records.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const paginationBtnStyle = (disabled) => ({
    minWidth: '40px',
    px: 2,
    py: 0.5,
    mx: 0.5,
    borderRadius: 1,
    textTransform: 'none',
    backgroundColor: disabled ? '#f5f5f5' : '#e0e0e0',
    color: disabled ? '#bdbdbd' : '#333',
    cursor: disabled ? 'default' : 'pointer',
    '&:hover': {
      backgroundColor: disabled ? '#f5f5f5' : '#bdbdbd',
    },
  });

  return (
    <Container maxWidth="lg" sx={{ mt: 10, px: 2 }}>
      <DateRangeSelector
        open={isDateRangeSelectorOpen}
        onClose={() => setDateRangeSelectorOpen(false)}
        onRangeSelected={handleDateRangeSelected}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          {username}
        </Typography>
        <IconButton color="primary" onClick={() => setDateRangeSelectorOpen(true)} sx={{ ml: 1 }}>
          <CalendarTodayIcon />
        </IconButton>
        <Typography variant="body1" sx={{ ml: 2, color: '#666' }}>
          {dayjs(startDate).format('YYYY.MM.DD')} — {dayjs(endDate).format('YYYY.MM.DD')}
        </Typography>
      </Box>

      {loading ? (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} variant="rectangular" width={150} height={90} sx={{ borderRadius: 2 }} />
            ))}
          </Box>
          <Skeleton variant="rectangular" width="100%" height={250} sx={{ borderRadius: 2, mb: 3 }} />
          <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2 }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Card sx={{ minWidth: 140, boxShadow: 3, borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="textSecondary">Revenue</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  {formatCurrency(totalIncome || 0)}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ minWidth: 140, boxShadow: 3, borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="textSecondary">Spent</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                  {`$${totalSpent}`}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ minWidth: 140, boxShadow: 3, borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="textSecondary">Profit</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: totalProfit >= 0 ? '#4caf50' : '#f44336' }}>
                  {formatCurrency(totalProfit || 0)}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ minWidth: 140, boxShadow: 3, borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="textSecondary">ROI</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: totalRoi < 0 ? '#f44336' : totalRoi < 30 ? '#ffeb3b' : '#4caf50' }}>
                  {totalRoi || 0}%
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ minWidth: 140, boxShadow: 3, borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="textSecondary">Reject</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                  {`$${reject || 0}`}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ mr: 2 }}>Динамика по дням</Typography>
              <Button size="small" sx={tabStyle(activeChartLine === 'both')} onClick={() => setActiveChartLine('both')}>
                Всё
              </Button>
              <Button size="small" sx={tabStyle(activeChartLine === 'profit')} onClick={() => setActiveChartLine('profit')}>
                Profit
              </Button>
              <Button size="small" sx={tabStyle(activeChartLine === 'revenue')} onClick={() => setActiveChartLine('revenue')}>
                Revenue
              </Button>
              <Button size="small" sx={tabStyle(activeChartLine === 'spent')} onClick={() => setActiveChartLine('spent')}>
                Spent
              </Button>
            </Box>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => `$${value}`} />
                <Legend />
                {(activeChartLine === 'both' || activeChartLine === 'profit') && (
                  <Line type="monotone" dataKey="Profit" stroke="#4caf50" strokeWidth={2} dot={{ r: 3 }} />
                )}
                {(activeChartLine === 'both' || activeChartLine === 'revenue') && (
                  <Line type="monotone" dataKey="Revenue" stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }} />
                )}
                {(activeChartLine === 'both' || activeChartLine === 'spent') && (
                  <Line type="monotone" dataKey="Spent" stroke="#ff9800" strokeWidth={2} dot={{ r: 3 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Revenue</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Spent Agn</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Spent Acc</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Profit</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>ROI</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRecords.map((record, index) => (
                  <TableRow key={index} sx={getRowStyle(record.profit)}>
                    <TableCell>{record.date}</TableCell>
                    <TableCell>{record.income}</TableCell>
                    <TableCell>{`$${record.expensesAgn}`}</TableCell>
                    <TableCell>{`$${record.expensesAcc}`}</TableCell>
                    <TableCell>{record.profit}</TableCell>
                    <TableCell>{record.Roi}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, mb: 2 }}>
              <Button 
                size="small" 
                sx={paginationBtnStyle(currentPage === 1)}
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                В начало
              </Button>
              <Button 
                size="small" 
                sx={paginationBtnStyle(currentPage === 1)}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                ← Назад
              </Button>
              <Typography sx={{ mx: 2, color: '#666' }}>
                Страница {currentPage} из {totalPages}
              </Typography>
              <Button 
                size="small" 
                sx={paginationBtnStyle(currentPage === totalPages)}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Вперёд →
              </Button>
              <Button 
                size="small" 
                sx={paginationBtnStyle(currentPage === totalPages)}
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                В конец
              </Button>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default BuyerDashboard;
